import { Security } from '../../proto/generated/security'
import { Watchlist_WatchlistItem, Watchlist_WatchlistItem_Type } from '../../proto/generated/watchlist'
import { Snapshot } from '../../reducer/security.reducer'
import { RootState } from '../../store'
import { snapshotWithIndicativePriceFromState, snapshotsWithIndicativePrices } from '../../util/indicativePrice'
import { calculateSnapshotDetails, getSecurityKey } from '../../util/security'
import { calculateSnapshotDetailsForExpression } from '../../util/expression'
import { toLocaleStringIn } from '../../util/general'
import { formatLargeNumber } from '../../util/screener'
import { green, red } from '../../assets/colors'
import { LiveMarketTextSelector } from '../common/LiveMarketText'

const displaySelectorCache = new Map<string, LiveMarketTextSelector>()
const colorSelectorCache = new Map<string, LiveMarketTextSelector>()

const getCachedSelector = (
    cache: Map<string, LiveMarketTextSelector>,
    cacheKey: string,
    factory: () => LiveMarketTextSelector
): LiveMarketTextSelector => {
    if (!cache.has(cacheKey)) {
        cache.set(cacheKey, factory())
    }
    return cache.get(cacheKey)!
}

const formatLtp = (ltp: number, format: 'locale' | 'fixed2'): string => {
    if (!Number.isFinite(ltp)) return '-'
    return format === 'locale' ? toLocaleStringIn(ltp) : ltp.toFixed(2)
}

const formatChange = (change: number): string => {
    if (!Number.isFinite(change)) return '-'
    return change.toFixed(2)
}

const formatChangePercent = (pct: number): string => {
    if (!Number.isFinite(pct)) return '-'
    return pct.toFixed(2) + '%'
}

const changeColorFromDelta = (change: number): string => {
    if (!Number.isFinite(change) || change === 0) return 'inherit'
    return change > 0 ? green : red
}

const computeSecurityStats = (
    state: RootState,
    secKey: string,
    security: Security
) => calculateSnapshotDetails(
    security,
    snapshotWithIndicativePriceFromState(state, secKey, state.securityStore.snapshots[secKey])
)

const computeExpressionStats = (
    state: RootState,
    expression: string,
    securities: Security[]
) => {
    const snapshots: Record<string, Snapshot> = {}
    const storeSnapshots = state.securityStore.snapshots
    for (const security of securities) {
        const key = getSecurityKey(security.exchange, security.code)
        if (storeSnapshots[key]) {
            snapshots[key] = storeSnapshots[key]
        }
    }
    return calculateSnapshotDetailsForExpression(
        expression,
        securities,
        snapshotsWithIndicativePrices(state, snapshots, securities)
    )
}

const buildSecuritySelectors = (
    secKey: string,
    security: Security,
    format: 'locale' | 'fixed2'
) => {
    const priceMultiplier = security.priceMultiplier || 100
    const prefix = `wl:${secKey}:${format}:${priceMultiplier}`

    const ltp = getCachedSelector(displaySelectorCache, `${prefix}:ltp`, () => (state) => {
        const stats = computeSecurityStats(state, secKey, security)
        return stats ? formatLtp(stats.ltp, format) : '-'
    })

    const ltpChange = getCachedSelector(displaySelectorCache, `${prefix}:chg`, () => (state) => {
        const stats = computeSecurityStats(state, secKey, security)
        return stats ? formatChange(stats.ltpChange) : '-'
    })

    const changePercent = getCachedSelector(displaySelectorCache, `${prefix}:pct`, () => (state) => {
        const stats = computeSecurityStats(state, secKey, security)
        return stats ? formatChangePercent(stats.changePercentage) : '-'
    })

    const volume = getCachedSelector(displaySelectorCache, `${prefix}:vol`, () => (state) => {
        const stats = computeSecurityStats(state, secKey, security)
        return stats?.volume ? formatLargeNumber(stats.volume) : '0'
    })

    const changeColor = getCachedSelector(colorSelectorCache, `${prefix}:color`, () => (state) => {
        const stats = computeSecurityStats(state, secKey, security)
        return stats ? changeColorFromDelta(stats.ltpChange) : 'inherit'
    })

    return { ltp, ltpChange, changePercent, volume, changeColor }
}

const buildExpressionSelectors = (
    expression: string,
    securities: Security[],
    format: 'locale' | 'fixed2'
) => {
    const exprKey = securities.map((s) => getSecurityKey(s.exchange, s.code)).join(',')
    const prefix = `wl:expr:${expression}:${exprKey}:${format}`

    const ltp = getCachedSelector(displaySelectorCache, `${prefix}:ltp`, () => (state) => {
        const stats = computeExpressionStats(state, expression, securities)
        return stats ? formatLtp(stats.ltp, format) : '-'
    })

    const ltpChange = getCachedSelector(displaySelectorCache, `${prefix}:chg`, () => (state) => {
        const stats = computeExpressionStats(state, expression, securities)
        return stats ? formatChange(stats.ltpChange) : '-'
    })

    const changePercent = getCachedSelector(displaySelectorCache, `${prefix}:pct`, () => (state) => {
        const stats = computeExpressionStats(state, expression, securities)
        return stats ? formatChangePercent(stats.changePercentage) : '-'
    })

    const volume = getCachedSelector(displaySelectorCache, `${prefix}:vol`, () => (state) => {
        const stats = computeExpressionStats(state, expression, securities)
        return stats?.volume ? formatLargeNumber(stats.volume) : '0'
    })

    const changeColor = getCachedSelector(colorSelectorCache, `${prefix}:color`, () => (state) => {
        const stats = computeExpressionStats(state, expression, securities)
        return stats ? changeColorFromDelta(stats.ltpChange) : 'inherit'
    })

    return { ltp, ltpChange, changePercent, volume, changeColor }
}

export type WatchlistLiveSelectors = {
    ltp: LiveMarketTextSelector
    ltpChange: LiveMarketTextSelector
    changePercent: LiveMarketTextSelector
    volume: LiveMarketTextSelector
    changeColor: LiveMarketTextSelector
}

export const buildWatchlistLiveSelectors = (
    item: Watchlist_WatchlistItem,
    security: Security | undefined,
    format: 'locale' | 'fixed2'
): WatchlistLiveSelectors | null => {
    if (item.type === Watchlist_WatchlistItem_Type.SECURITY) {
        const sec = security ?? item.security
        if (!sec) return null
        const secKey = getSecurityKey(sec.exchange, sec.code)
        return buildSecuritySelectors(secKey, sec, format)
    }
    if (item.type === Watchlist_WatchlistItem_Type.EXPRESSION && item.securityExpression) {
        return buildExpressionSelectors(
            item.securityExpression.expression,
            item.securityExpression.securities,
            format
        )
    }
    return null
}
